import { useEffect, useRef, useState } from 'react';

import type { SiteCmsRichNode } from '@shared/types/entities';
import type { SiteCmsCompetitionCard, SiteCmsDocumentResponse, SiteCmsPageDraft } from '@shared/types/entities';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { toast } from 'sonner';

import { richText } from '../../../../../packages/site-renderer/src/sections-render.mjs';
import SiteCmsLinkPicker from './SiteCmsLinkPicker';

const escape = (value: unknown) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
function safeLink(value: unknown): string {
  const v = String(value ?? '');
  if (/\s|\\/.test(v)) return '#';
  if (
    v.startsWith('/') &&
    !v.startsWith('//') &&
    !v.includes('%') &&
    !v.split(/[/?#]/).some(part => part === '..' || part === '.')
  )
    return v;
  try {
    const url = new URL(v);
    if (url.protocol === 'https:' && !url.username && !url.password) return url.href;
  } catch {
    /* invalid link */
  }
  return '#';
}

type Props = {
  resources?: {
    pages: SiteCmsPageDraft[];
    documents: SiteCmsDocumentResponse[];
    competitions: SiteCmsCompetitionCard[];
  };
  text: string;
  rich?: SiteCmsRichNode;
  onChange: (text: string, rich: SiteCmsRichNode) => void;
};
/** Existing Tiptap dependency; only paragraphs, bullets, emphasis and safe links. */
export default function SiteCmsRichTextEditor({ text, rich, onChange, resources }: Props) {
  const callback = useRef(onChange);
  useEffect(() => {
    callback.current = onChange;
  }, [onChange]);
  const [linkOpen, setLinkOpen] = useState(false);
  const [href, setHref] = useState('https://');
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        code: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        strike: false,
        underline: false,
        orderedList: false,
        link: { openOnClick: false, autolink: false, linkOnPaste: false },
      }),
    ],
    content: rich ?? richText(text, escape, value => escape(safeLink(value))),
    editorProps: {
      attributes: {
        role: 'textbox',
        'aria-label': 'Texte de la section',
        'aria-multiline': 'true',
        class: 'cms-rich-prose',
      },
    },
    onUpdate: ({ editor: instance }) => {
      callback.current(instance.getText({ blockSeparator: '\n\n' }), instance.getJSON() as SiteCmsRichNode);
    },
  });
  // Synchronize the external editor (not React state), for undo/reload/page switch.
  useEffect(() => {
    if (!editor) return;
    if (rich) {
      if (JSON.stringify(editor.getJSON()) !== JSON.stringify(rich))
        editor.commands.setContent(rich, { emitUpdate: false });
    } else if (editor.getText({ blockSeparator: '\n\n' }) !== text) {
      editor.commands.setContent(
        richText(text, escape, value => escape(safeLink(value))),
        { emitUpdate: false },
      );
    }
  }, [editor, rich, text]);
  if (!editor) return <p className='cms-hint'>Chargement de l’éditeur…</p>;
  return (
    <div className='cms-rich-input'>
      <div className='cms-rich-toolbar' role='group' aria-label='Mise en forme du texte'>
        <button
          type='button'
          aria-pressed={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <b>Gras</b>
        </button>
        <button
          type='button'
          aria-pressed={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <i>Italique</i>
        </button>
        <button
          type='button'
          aria-pressed={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          Liste
        </button>
        <button
          type='button'
          onClick={() => {
            setHref(String(editor.getAttributes('link').href ?? 'https://'));
            setLinkOpen(true);
          }}
        >
          Lien
        </button>
        <button
          type='button'
          disabled={!editor.isActive('link')}
          onClick={() => editor.chain().focus().unsetLink().run()}
        >
          Retirer le lien
        </button>
      </div>
      {linkOpen && (
        <div className='cms-row'>
          {resources ? (
            <SiteCmsLinkPicker value={href} onChange={setHref} {...resources} />
          ) : (
            <label>
              Adresse du lien
              <input value={href} onChange={e => setHref(e.target.value)} placeholder='/association/ ou https://…' />
            </label>
          )}
          <button
            type='button'
            className='cms-button'
            onClick={() => {
              const safe = safeLink(href);
              if (safe === '#') {
                toast.error('Utilisez une page interne ou une adresse HTTPS.');
                return;
              }
              editor.chain().focus().extendMarkRange('link').setLink({ href: safe }).run();
              setLinkOpen(false);
            }}
          >
            Appliquer
          </button>
          <button type='button' className='cms-link-button' onClick={() => setLinkOpen(false)}>
            Annuler
          </button>
        </div>
      )}
      <EditorContent editor={editor} />
      <small className={text.length > 5000 ? 'cms-danger' : ''}>
        {text.length}/5 000 caractères
        {text.length > 5000
          ? ' — raccourcissez le texte avant d’enregistrer.'
          : ' · Sans HTML ni scripts personnalisés.'}
      </small>
    </div>
  );
}
